import React from 'react';
import classnames from 'classnames';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { Link } from 'react-router-dom';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles } from '@material-ui/core/styles';

import roadsideIcon from '../../assets/img/icon-roadside.png';
import rentalIcon from '../../assets/img/icon-rental.png';
import petIcon from '../../assets/img/icon-pet.png';

const useStyles = makeStyles((theme) => {
  const {
    palette: { tenant },
    breakpoints,
  } = theme;

  return {
    container: {
      width: '100%',
      padding: 24,
      [breakpoints.up('sm')]: {
        padding: 56,
      },
    },
    grid: {
      margin: '0 auto',
      [breakpoints.up('sm')]: {
        padding: '32px 0',
      },
    },
    gridBox: {
      minWidth: 272,
      paddingTop: 70,
      alignItems: 'center',
      margin: '16px 0',
      [breakpoints.up('sm')]: {
        margin: 16,
        padding: '100px 24px 50px 24px',
        width: 360,
        height: 396,
      },
    },
    headerText: {
      fontSize: 32,
      [breakpoints.up('sm')]: {
        margin: '0 auto',
      },
      [breakpoints.between('sm', 'md')]: {
        maxWidth: 470,
      },
    },
    subHeaderText: {
      fontSize: 15,
      [breakpoints.up('sm')]: {
        fontSize: 20,
      },
      [breakpoints.between('sm', 'md')]: {
        margin: '0 auto',
        maxWidth: 470,
      },
    },
    imgContainer: {
      height: 75,
    },
    imgContainerOffset: {
      paddingTop: 20,
    },
    getHelpLink: {
      color: tenant.primaryHighlight,
      fontWeight: 'bold',
      fontSize: 20,
      marginTop: 20,
      [breakpoints.up('sm')]: {
        marginTop: 32,
      },
    },
    cardDescriptionText: {
      color: tenant.mutedDark,
    },
  };
});

const POLICY_COVERAGE_DATA = [
  {
    img: {
      src: roadsideIcon,
      alt: 'Roadside assistance',
      offset: false,
    },
    title: 'Roadside assistance',
    description:
      'Add this coverage to simply request help like an emergency tow, a dead battery jump-start or a tire change from the Farmers app. Our system will pinpoint your location using GPS - you can even track the progress of the nearest service vehicle.',
    link: {
      cta: 'Get help now',
      path: 'claims/new-claim',
    },
  },
  {
    img: {
      src: rentalIcon,
      alt: 'Rental car coverage',
      offset: true,
    },
    title: 'Rental car coverage',
    description:
      'For just pennies a day, you can add loss of use coverage to a comprehensive and collision policy. It provides up to 20 days and $1,000 in coverage for a rental car - or rideshare costs - while your car is in the shop.',
    link: null,
  },
  {
    img: {
      src: petIcon,
      alt: 'Medical care for your pet',
      offset: false,
    },
    title: 'Medical care for your pet',
    description:
      "Your pet is part of your family too. And if it's injured in a car accident, your auto policy with comprehensive and collision coverage will pay vet bills up to the specified limit.",
    link: null,
  },
];

function CustomizePolicySection() {
  const classes = useStyles();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  return (
    <Container maxWidth={false} className={classes.container}>
      <Typography
        variant="h3"
        align="center"
        color="textPrimary"
        className={classes.headerText}
        gutterBottom
      >
        Customize your policy to get the coverage you want
      </Typography>
      <Typography
        variant="h5"
        align="center"
        color="textSecondary"
        className={classes.subHeaderText}
        gutterBottom
      >
        Here are just a few of the special features you can include in a Farmers
        auto policy:
      </Typography>
      <Grid
        container
        direction={isDesktop ? 'row' : 'column'}
        alignItems="center"
        justify="center"
        className={classes.grid}
      >
        {POLICY_COVERAGE_DATA.map(({ img, title, description, link }, i) => (
          <Paper key={title} className={classes.gridBox} elevation={0}>
            <Box
              className={classnames(
                classes.imgContainer,
                img.offset && classes.imgContainerOffset
              )}
            >
              <img src={img.src} alt={img.alt} />
            </Box>
            <Typography variant="h4" color="primary">
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              color="primary"
              className={classes.cardDescriptionText}
              gutterBottom
            >
              {description}
            </Typography>
            {!!link && (
              <Link to={link.path} className={classes.getHelpLink}>
                {link.cta}
              </Link>
            )}
          </Paper>
        ))}
      </Grid>
    </Container>
  );
}

export default CustomizePolicySection;
